import { useMemo, useState } from "react";
import { Loader2, CheckCircle2, Upload, FileSpreadsheet, Sparkles, AlertTriangle, Copy, ArrowLeft, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const SEL = "bg-[#0b0811] border border-white/10 text-white rounded px-1.5 py-1 text-[11px] h-7 w-full";
const INP = "bg-[#0b0811] border-white/10 text-white text-[11px] h-7 px-1.5";

function Confidence({ v }) {
  const pct = Math.round((v || 0) * 100);
  const color = pct >= 80 ? "#22c55e" : pct >= 60 ? "#f59e0b" : "#ef4444";
  return <span className="text-[10px] font-bold" style={{ color }} title="Confiance IA">{pct}%</span>;
}

function PreviewRow({ r, config, onChange }) {
  const set = (k, v) => onChange({ ...r, [k]: v });
  const dim = !r.include || r.duplicate;
  return (
    <tr className={`border-b border-white/5 ${dim ? "opacity-50" : ""}`} data-testid={`import-row-${r.row_id}`}>
      <td className="p-1 text-center"><input type="checkbox" checked={!!r.include} onChange={e => set("include", e.target.checked)} className="accent-[#f7c935]" data-testid={`import-row-${r.row_id}-include`} /></td>
      <td className="p-1 text-[10px] text-slate-500 max-w-[110px] truncate" title={r.section}>{r.section}</td>
      <td className="p-1 min-w-[160px]">
        <Input value={r.element || ""} onChange={e => set("element", e.target.value)} className={INP} data-testid={`import-row-${r.row_id}-element`} />
        <div className="text-[9px] text-slate-600 truncate max-w-[200px]" title={r.element_raw}>{r.element_raw}</div>
      </td>
      <td className="p-1 min-w-[130px]">
        <select value={r.category} onChange={e => set("category", e.target.value)} className={SEL} data-testid={`import-row-${r.row_id}-category`}>
          {(config.categories || []).map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </td>
      <td className="p-1 min-w-[100px]">
        <select value={r.status} onChange={e => set("status", e.target.value)} className={SEL} data-testid={`import-row-${r.row_id}-status`}>
          {(config.statuses || []).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </td>
      <td className="p-1"><Input type="date" value={r.echeance || ""} onChange={e => set("echeance", e.target.value)} className={INP + " w-[118px]"} data-testid={`import-row-${r.row_id}-echeance`} /></td>
      <td className="p-1"><Input type="number" step="0.01" value={r.solde_ttc} onChange={e => set("solde_ttc", parseFloat(e.target.value) || 0)} className={INP + " w-[90px] text-right"} data-testid={`import-row-${r.row_id}-amount`} /></td>
      <td className="p-1">
        <select value={r.modalite_paiement || ""} onChange={e => set("modalite_paiement", e.target.value)} className={SEL + " w-[100px]"}>
          <option value="">—</option>
          {["virement", "prelevement", "cb", "cheque", "especes", "lcr"].map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </td>
      <td className="p-1"><Input value={r.compte || ""} onChange={e => set("compte", e.target.value)} className={INP + " w-[80px]"} placeholder="Compte" /></td>
      <td className="p-1"><Input value={r.facture_ref || ""} onChange={e => set("facture_ref", e.target.value)} className={INP + " w-[100px]"} placeholder="Réf." /></td>
      <td className="p-1 text-center whitespace-nowrap">
        <Confidence v={r.confidence} />
        {r.duplicate && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-400" title="Déjà présent dans la base" data-testid={`import-row-${r.row_id}-duplicate`}><Copy className="w-2.5 h-2.5 inline" /> doublon</span>}
        {r.warnings?.length > 0 && <AlertTriangle className="w-3 h-3 inline ml-1 text-amber-400" title={r.warnings.join(" · ")} />}
      </td>
    </tr>
  );
}

export default function DepensesImportDialog({ open, onClose, api, config, currentMonth, onImported }) {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("file");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState("");
  const [classeur, setClasseur] = useState("");
  const [filterSection, setFilterSection] = useState("");
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setStep("file"); setError(""); setAnalysis(null); setRows([]); setResult(null); setFilterSection(""); };
  const handleClose = () => { reset(); onClose(); };

  const analyze = async () => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("period", currentMonth || "");
      const r = await api.post("/depenses/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(r.data); setRows(r.data.rows); setPeriod(r.data.period); setClasseur(r.data.title || file.name.replace(/\.xlsx$/i, ""));
      setStep("preview");
    } catch (e) { setError(e.response?.data?.detail || e.message || "Analyse impossible"); }
    setBusy(false);
  };

  const execute = async () => {
    setBusy(true); setError("");
    try {
      const r = await api.post("/depenses/import/execute", { import_id: analysis.import_id, rows, period, classeur, skip_duplicates: true });
      setResult(r.data); setStep("done"); onImported?.(r.data);
    } catch (e) { setError(e.response?.data?.detail || "Import impossible"); }
    setBusy(false);
  };

  const updateRow = (row) => setRows(rs => rs.map(x => x.row_id === row.row_id ? row : x));
  const toggleAll = (v) => setRows(rs => rs.map(x => (!filterSection || x.section === filterSection) ? { ...x, include: v && !x.duplicate } : x));

  const visible = useMemo(() => rows.filter(r => !filterSection || r.section === filterSection), [rows, filterSection]);
  const selected = rows.filter(r => r.include && !r.duplicate);
  const selectedTotal = selected.reduce((s, r) => s + (parseFloat(r.solde_ttc) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className={`bg-[#14101c] border-white/10 text-white ${step === "preview" ? "max-w-[96vw] xl:max-w-7xl" : "max-w-lg"} max-h-[92vh] overflow-hidden flex flex-col`} data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#f7c935]" /> Import IA d'un classeur de charges
            {step === "preview" && <span className="text-xs font-normal text-slate-500 ml-2">Étape 2/3 — vérifiez puis validez</span>}
          </DialogTitle>
        </DialogHeader>

        {step === "file" && (
          <div className="space-y-4">
            <p className="text-xs text-slate-400">Déposez votre export Excel (ex. <i>Charges EMS 09/26</i>). Les sections, statuts, échéances et montants sont lus dans le fichier ; l'IA nettoie les libellés et classe chaque ligne (catégorie, statut, paiement) avec un indice de confiance. Rien n'est enregistré avant votre validation.</p>
            <label className="block border-2 border-dashed border-white/10 hover:border-[#f7c935]/40 rounded-xl p-8 text-center cursor-pointer transition-colors" data-testid="import-dropzone">
              <input type="file" accept=".xlsx,.xlsm" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
              <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-500 mb-2" />
              {file ? <div className="text-sm font-bold text-[#f7c935]" data-testid="import-file-name">{file.name}</div> : <div className="text-sm text-slate-400">Cliquez pour choisir un fichier .xlsx</div>}
            </label>
            {error && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2" data-testid="import-error">{error}</div>}
            <Button onClick={analyze} disabled={!file || busy} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="import-analyze-btn">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analyse IA en cours (10 à 30 s)…</> : <><Upload className="w-4 h-4 mr-2" /> Analyser le fichier</>}
            </Button>
          </div>
        )}

        {step === "preview" && analysis && (
          <div className="flex flex-col min-h-0 flex-1 gap-3">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs" data-testid="import-stats">
              {[["Lignes", analysis.stats.rows], ["Sélection", selected.length], ["Doublons", analysis.stats.duplicates], ["Confiance faible", analysis.stats.low_confidence], ["Total fichier", `${fmt(analysis.stats.total_ttc)} €`], ["Total sélection", `${fmt(selectedTotal)} €`]].map(([l, v]) => (
                <div key={l} className="bg-[#0b0811] rounded-lg p-2"><div className="text-[9px] uppercase text-slate-500">{l}</div><div className="font-bold text-sm">{v}</div></div>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-3 text-xs">
              <div><label className="text-[10px] uppercase text-slate-500 block mb-0.5">Période</label><Input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="bg-[#0b0811] border-white/10 text-white h-8 w-40" data-testid="import-period" /></div>
              <div className="flex-1 min-w-[200px]"><label className="text-[10px] uppercase text-slate-500 block mb-0.5">Classeur</label><Input value={classeur} onChange={e => setClasseur(e.target.value)} className="bg-[#0b0811] border-white/10 text-white h-8" data-testid="import-classeur" /></div>
              <div><label className="text-[10px] uppercase text-slate-500 block mb-0.5">Section</label>
                <select value={filterSection} onChange={e => setFilterSection(e.target.value)} className="bg-[#0b0811] border border-white/10 text-white rounded px-2 h-8 text-xs" data-testid="import-section-filter">
                  <option value="">Toutes ({rows.length})</option>
                  {analysis.sections.map(s => <option key={s.name} value={s.name}>{s.name} ({s.rows}){s.mismatch ? " ⚠" : ""}</option>)}
                </select>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => toggleAll(true)} className="border-white/10 text-slate-300 h-8 text-xs" data-testid="import-select-all">Tout cocher</Button>
                <Button size="sm" variant="outline" onClick={() => toggleAll(false)} className="border-white/10 text-slate-300 h-8 text-xs" data-testid="import-select-none">Tout décocher</Button>
              </div>
            </div>
            {analysis.sections.some(s => s.mismatch) && (
              <div className="text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2 flex items-center gap-2" data-testid="import-mismatch-warning">
                <AlertTriangle className="w-3.5 h-3.5" /> Écart entre le total de section du fichier et la somme des lignes pour : {analysis.sections.filter(s => s.mismatch).map(s => `${s.name} (${fmt(s.total)} vs ${fmt(s.rows_total)})`).join(", ")}
              </div>
            )}
            <div className="overflow-auto flex-1 min-h-0 border border-white/5 rounded-lg" data-testid="import-preview-table">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[#0b0811] text-[10px] uppercase text-slate-500 z-10">
                  <tr>
                    <th className="p-1.5 w-8"><Check className="w-3 h-3 mx-auto" /></th><th className="p-1.5 text-left">Section</th><th className="p-1.5 text-left">Élément</th><th className="p-1.5 text-left">Catégorie</th>
                    <th className="p-1.5 text-left">Statut</th><th className="p-1.5 text-left">Échéance</th><th className="p-1.5 text-right">Montant TTC</th><th className="p-1.5 text-left">Paiement</th><th className="p-1.5 text-left">Compte</th><th className="p-1.5 text-left">Réf.</th><th className="p-1.5">IA</th>
                  </tr>
                </thead>
                <tbody>{visible.map(r => <PreviewRow key={r.row_id} r={r} config={config} onChange={updateRow} />)}</tbody>
              </table>
            </div>
            {error && <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-2" data-testid="import-error">{error}</div>}
            <div className="flex items-center justify-between gap-2 pt-1">
              <Button variant="ghost" onClick={reset} className="text-slate-400 text-xs" data-testid="import-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Autre fichier</Button>
              <div className="text-xs text-slate-500">{analysis.stats.duplicates > 0 && `${analysis.stats.duplicates} doublon(s) ignoré(s) · `}Les dépenses validées alimentent la comptabilité automatique.</div>
              <Button onClick={execute} disabled={busy || selected.length === 0} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="import-execute-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Importer {selected.length} dépense(s) — {fmt(selectedTotal)} €
              </Button>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="space-y-4" data-testid="import-result">
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
              <div>
                <div className="font-bold text-green-400" data-testid="import-result-created">{result.created} dépense(s) importée(s) dans « {result.classeur} » ({result.period})</div>
                <div className="text-xs text-slate-400 mt-1">{result.posted} écriture(s) comptable(s) générée(s){result.accounting_errors?.length ? ` · ${result.accounting_errors.length} erreur(s) comptable(s)` : ""}</div>
              </div>
            </div>
            {result.skipped?.length > 0 && (
              <div className="text-xs bg-[#0b0811] rounded-lg p-3 max-h-40 overflow-y-auto" data-testid="import-result-skipped">
                <div className="font-bold text-amber-400 mb-1">{result.skipped.length} ligne(s) ignorée(s)</div>
                {result.skipped.map((s, i) => <div key={i} className="text-slate-400">• {s.element || s.row_id} — {s.reason}</div>)}
              </div>
            )}
            <Button onClick={handleClose} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="import-close-btn">Fermer</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
